import { Badge } from './Badge';

/**
 * Invoice status → badge, over the REAL Postgres invoice-status enum
 * (`draft | open | paid | partial_refunded | refunded | uncollectible | void`
 * — see api money.ts INVOICE_STATUS). Uses a `Record<string, …>` + fallback so a
 * status the UI hasn't seen still renders (never assume the lookup hits). Keep in
 * sync with owner/InvoiceDetail.tsx, which shares these labels.
 */
type Tone = 'success' | 'warning' | 'error' | 'info' | 'neutral';

const INVOICE_STATUS_CONFIG: Record<string, { tone: Tone; label: string }> = {
  draft: { tone: 'neutral', label: 'Draft' },
  open: { tone: 'warning', label: 'Unpaid' },
  paid: { tone: 'success', label: 'Paid' },
  partial_refunded: { tone: 'warning', label: 'Partly refunded' },
  refunded: { tone: 'neutral', label: 'Refunded' },
  uncollectible: { tone: 'error', label: 'Uncollectible' },
  void: { tone: 'neutral', label: 'Void' },
  // legacy/mock alias
  past_due: { tone: 'warning', label: 'Past due' },
};

const FALLBACK: { tone: Tone; label: string } = { tone: 'neutral', label: 'Unknown' };

/** Human label for an invoice status (humanizes any unknown value). */
export function invoiceStatusLabel(status: string): string {
  return INVOICE_STATUS_CONFIG[status]?.label ?? status.replace(/_/g, ' ');
}

export function InvoiceStatusBadge({ status }: { status: string }) {
  const config = INVOICE_STATUS_CONFIG[status] ?? { ...FALLBACK, label: status.replace(/_/g, ' ') };
  return <Badge tone={config.tone}>{config.label}</Badge>;
}

/**
 * Payment-provider slug → display label. Providers are the raw enum values the
 * API stores (`sslcommerz | stripe | paypal | manual_cash | bkash | nagad |
 * bank_transfer`), plus a couple of ledger-only synonyms. Unknown → the raw slug.
 */
const PROVIDER_LABEL: Record<string, string> = {
  sslcommerz: 'SSLCommerz',
  stripe: 'Stripe',
  paypal: 'PayPal',
  manual_cash: 'Cash',
  cash: 'Cash',
  bkash: 'bKash',
  nagad: 'Nagad',
  bank_transfer: 'Bank transfer',
  credit_applied: 'Account credit',
};

export function providerLabel(provider: string | null | undefined): string {
  if (!provider) return '—';
  return PROVIDER_LABEL[provider] ?? provider;
}
